import React from 'react'
import { kebabCase } from 'lodash'

import EntryMarker from 'marketing-site/src/components/common/EntryMarker'
import * as Utils from 'marketing-site/src/library/utils'
import { transformImage } from 'marketing-site/src/transformers/elements/ContentfulImage'
import {
  ICtaLink,
  IGatedContent as IContentfulGatedContent,
  ISignupCta,
  ITwoCtAsHeroElement,
} from 'marketing-site/@types/generated/contentful'
import { Entry } from 'contentful'
import {
  GatedContent,
  IProps as IGatedContent,
} from 'marketing-site/src/library/components/GatedContent'
import { CTA } from 'marketing-site/src/transformers/CTA'
import { transformCtaHeroElement } from './ContentfulHeroWithCTA'
import { transformLogoParty } from './ContentfulLogoParty'

export const ContentfulGatedContent = (
  gatedContent: IContentfulGatedContent,
): React.ReactElement => (
  <EntryMarker entry={gatedContent}>
    {() => <GatedContent {...transformGatedContent(gatedContent)} />}
  </EntryMarker>
)

interface ITwoCTAs {
  renderCta1: () => React.ReactNode
  renderCta2?: () => React.ReactNode
}

const TwoCTAs = (twoCTAs: ITwoCTAs) => (
  <div className="gated-content__ctas-wrapper">
    {twoCTAs.renderCta1()}
    {twoCTAs.renderCta2 && twoCTAs.renderCta2()}
    <style jsx>{`
      .gated-content__ctas-wrapper {
        display: flex;
        justify-content: center;

        :global(& > *:not(:first-child)) {
          margin-left: 0.75rem;
        }
      }
    `}</style>
  </div>
)

const transformSignUpCTA = (signUpCTA: IContentfulGatedContent['fields']['signUpCta']) => {
  switch (signUpCTA.sys.contentType.sys.id) {
    case 'twoCtAsHeroElement':
      const twoCTAs = transformCtaHeroElement(signUpCTA as ITwoCtAsHeroElement)
      return () => <TwoCTAs {...twoCTAs} />
    default:
      return () => <CTA {...(signUpCTA as ICtaLink | ISignupCta)} />
  }
}

export function transformGatedContent({ fields }: IContentfulGatedContent): IGatedContent {
  return {
    ...fields,
    image: fields.image && transformImage(fields.image),
    enableBorders: fields.enableBorders || false,
    bgColor: fields.bgColor ? Utils.getHexColorFromName(fields.bgColor) : undefined,
    type:
      fields.signUpCta &&
      (kebabCase(fields.signUpCta.sys.contentType.sys.id) as IGatedContent['type']),
    renderEmailForm: fields.signUpCta && transformSignUpCTA(fields.signUpCta),
    logoParty: fields.logoParty && transformLogoParty(fields.logoParty),
    icon: fields.icon && fields.icon.fields.file.url,
    headingFontStyle: fields.headingStyle && Utils.getTypedHeadingStyle(fields.headingStyle),
  }
}

export function isGatedContent(entry: Entry<any>): entry is IContentfulGatedContent {
  return entry.sys.contentType?.sys.id === 'gatedContent'
}
